import {
  SAVED_FILTER_EXCLUDED_QUERY_KEYS,
  SAVED_FILTER_MAX_NAME_LENGTH,
  SAVED_FILTER_MAX_PATHNAME_LENGTH,
  SAVED_FILTER_MAX_QUERY_STRING_LENGTH,
} from './types/saved-filters';

export class SavedFilterNormalizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SavedFilterNormalizationError';
  }
}

/**
 * Canonical SPA pathname for saved-filter scoping.
 * e.g. "/runs", "/settings/api-keys"
 */
export function normalizePathname(raw: string): string {
  if (typeof raw !== 'string') {
    throw new SavedFilterNormalizationError('pathname must be a string');
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new SavedFilterNormalizationError('pathname is required');
  }
  if (!trimmed.startsWith('/')) {
    throw new SavedFilterNormalizationError('pathname must start with /');
  }
  const withoutQueryHash = trimmed.split(/[?#]/)[0] ?? trimmed;
  if (!withoutQueryHash.startsWith('/')) {
    throw new SavedFilterNormalizationError('pathname must start with /');
  }
  let pathname = withoutQueryHash;
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1);
  }
  if (!pathname) {
    pathname = '/';
  }
  if (pathname.length > SAVED_FILTER_MAX_PATHNAME_LENGTH) {
    throw new SavedFilterNormalizationError(
      `pathname must be at most ${SAVED_FILTER_MAX_PATHNAME_LENGTH} characters`
    );
  }
  return pathname;
}

/** Trim and collapse internal whitespace for display names. */
export function normalizeName(raw: string): string {
  if (typeof raw !== 'string') {
    throw new SavedFilterNormalizationError('name must be a string');
  }
  const trimmed = raw.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    throw new SavedFilterNormalizationError('name is required');
  }
  if (trimmed.length > SAVED_FILTER_MAX_NAME_LENGTH) {
    throw new SavedFilterNormalizationError(
      `name must be at most ${SAVED_FILTER_MAX_NAME_LENGTH} characters`
    );
  }
  return trimmed;
}

/**
 * Normalize a URL query string for storage.
 * - Strips leading "?"
 * - Removes paging: `page`, `limit` and `pageSize`
 * - Retains `cols`, sort, filters, etc.
 * - Allows empty string (default view)
 */
export function normalizeQueryString(raw: string): string {
  if (typeof raw !== 'string') {
    throw new SavedFilterNormalizationError('queryString must be a string');
  }
  let s = raw.trim();
  if (s.startsWith('?')) {
    s = s.slice(1);
  }
  const params = new URLSearchParams(s);
  for (const key of SAVED_FILTER_EXCLUDED_QUERY_KEYS) {
    params.delete(key);
  }
  const out = params.toString();
  if (out.length > SAVED_FILTER_MAX_QUERY_STRING_LENGTH) {
    throw new SavedFilterNormalizationError(
      `queryString must be at most ${SAVED_FILTER_MAX_QUERY_STRING_LENGTH} characters`
    );
  }
  return out;
}

/** Stable query form for equality: excluded keys dropped, remaining pairs sorted. */
export function canonicalizeQueryString(raw: string): string {
  const normalized = normalizeQueryString(raw);
  const params = new URLSearchParams(normalized);
  const entries = [...params.entries()].sort((a, b) => {
    const byKey = a[0].localeCompare(b[0]);
    return byKey !== 0 ? byKey : a[1].localeCompare(b[1]);
  });
  return new URLSearchParams(entries).toString();
}

/** True when a saved query already matches the current page (ignoring page, limit, pageSize and order). */
export function savedFilterQueryMatchesCurrent(
  savedQueryString: string,
  currentQueryString: string
): boolean {
  try {
    return (
      canonicalizeQueryString(savedQueryString) ===
      canonicalizeQueryString(currentQueryString)
    );
  } catch {
    return false;
  }
}

type SavedFilterQueryRef = { id: string; queryString: string };

export function matchingSavedFilters<T extends SavedFilterQueryRef>(
  filters: T[],
  currentQueryString: string
): T[] {
  return filters.filter((filter) =>
    savedFilterQueryMatchesCurrent(filter.queryString, currentQueryString)
  );
}

/**
 * Pin the matching saved filter so Overwrite still targets it after the URL
 * drifts. Keep the previous pin when nothing matches.
 */
export function nextPinnedSavedFilterId(
  filters: SavedFilterQueryRef[],
  currentQueryString: string,
  pinnedId: string | null
): string | null {
  const matching = matchingSavedFilters(filters, currentQueryString);
  if (matching.length === 0) {
    if (pinnedId && filters.some((filter) => filter.id === pinnedId)) {
      return pinnedId;
    }
    return null;
  }
  if (pinnedId && matching.some((filter) => filter.id === pinnedId)) {
    return pinnedId;
  }
  return matching[0]?.id ?? null;
}

/** Saved filter the page is treating as applied (current match, else pin). */
export function resolveAppliedSavedFilter<T extends SavedFilterQueryRef>(
  filters: T[],
  currentQueryString: string,
  pinnedId: string | null
): T | null {
  const matching = matchingSavedFilters(filters, currentQueryString);
  if (matching.length === 1) return matching[0] ?? null;
  if (matching.length > 1) {
    return matching.find((filter) => filter.id === pinnedId) ?? matching[0] ?? null;
  }
  if (!pinnedId) return null;
  return filters.find((filter) => filter.id === pinnedId) ?? null;
}

/** Build a full path + query for applying a saved filter. */
export function buildSavedFilterHref(
  pathname: string,
  queryString: string
): string {
  const path = normalizePathname(pathname);
  const query = normalizeQueryString(queryString);
  return query ? `${path}?${query}` : path;
}

/**
 * Omni-search match for a saved view: filter name, pathname, or hyphenated
 * path treated as words (`/settings/api-keys` matches "api keys").
 */
export function savedFilterMatchesOmniQuery(
  name: string,
  pathname: string,
  query: string
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const pathWords = pathname.replace(/-/g, ' ').toLowerCase();
  return (
    name.toLowerCase().includes(q) ||
    pathname.toLowerCase().includes(q) ||
    pathWords.includes(q)
  );
}
